import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import instance from 'utils/apiCalls';
import { RouteItems } from 'sitemap';

interface PermissionData {
  message: string;
  data: {
    role_id: string;
    pages: {
      page_id: number;
      page_name: string;
      actions: {
        action_id: number;
        action: string;
      }[];
    }[];
  };
}

interface AdminRolePermissionContextType {
  allowedPageNames: Set<string>;
  allowedActionsByPage: Map<string, Set<string>>;
  loading: boolean;
  canViewPage: (pageName: string) => boolean;
  canPerformAction: (pageName: string, actionKey: string) => boolean;
  filterSitemapRoutesByPermissions: (routes: RouteItems[]) => RouteItems[];
}

const AdminRolePermissionContext = createContext<AdminRolePermissionContextType | undefined>(undefined);

export const getRequiredActionForPathname = (pathname: string): { pageName: string; action: string } | null => {
  if (pathname.startsWith('/builder-subscription/edit/')) return { pageName: 'Builder Subscription', action: 'edit' };
  if (pathname === '/builder-subscription/form') return { pageName: 'Builder Subscription', action: 'add' };
  if (pathname.startsWith('/subscription/analytics/')) return { pageName: 'Subscription', action: 'analytics' };
  return null;
};

export const getPermissionPageNameForPathname = (pathname: string): string | null => {
  if (pathname.startsWith('/user-table')) return 'Users';
  if (pathname.startsWith('/property/add-property') || pathname.startsWith('/property/add/')) return 'Add Property';
  if (pathname.startsWith('/property/property-report')) return 'Property Report';
  if (pathname.startsWith('/property')) return 'Property Listing';
  if (pathname.startsWith('/project/project-promotion') || pathname.startsWith('/project/project-promotion/form/')) return 'Project Promotion';
  if (pathname.startsWith('/project/link-video')) return 'Project Link Video';
  if (pathname.startsWith('/project-co-investment')) return 'Project Co-Investment';
  if (pathname.startsWith('/project') || pathname.startsWith('/project/form/')) return 'Project';
  if (pathname.startsWith('/enquiry/project-inquiry')) return 'Project Enquiry';
  if (pathname.startsWith('/enquiry/properties-enquiry')) return 'Property Enquiry';
  if (pathname.startsWith('/enquiry/user-requirements')) return 'User Requirements';
  if (pathname.startsWith('/enquiry/soldout-inquiry')) return 'SoldOut Inquiry';
  if (pathname.startsWith('/enquiry')) return 'Enquiry';
  if (pathname.startsWith('/potential-buyer')) return 'Potential Buyer';
  if (pathname.startsWith('/developer') || pathname.startsWith('/developer/form/')) return 'Developer';
  if (pathname.startsWith('/locality') || pathname.startsWith('/locality/form/')) return 'Locality';
  if (pathname.startsWith('/blogs') || pathname.startsWith('/blogs/form')) return 'Blogs';
  if (pathname.startsWith('/seo-content')) return 'SEO Content';
  if (pathname.startsWith('/subscription') || pathname.startsWith('/subscription/form')) return 'Subscription';
  if (pathname.startsWith('/message-service/whatsapp-send')) return 'Whatsapp Send';
  if (pathname.startsWith('/message-service/campaign') || pathname === '/message-service') return 'Campaigns';
  if (pathname.startsWith('/message-service/cron')) return 'Crons';
  if (pathname.startsWith('/message-service/templates')) return 'Templates';
  if (pathname === '/admin-users') return 'Admin Users';
  if (pathname === '/roles-permissions') return 'Roles & Permission';
  if (pathname === '/chatinfo') return 'Chat Info';
  if (pathname.startsWith('/builder-subscription')) return 'Builder Subscription';
  return null;
};

export const isHomePath = (pathname: string): boolean => {
  return pathname === '/' || pathname === '/user-table';
};

export const useAdminRolePermissions = () => {
  const context = useContext(AdminRolePermissionContext);
  if (context === undefined) {
    throw new Error('useAdminRolePermissions must be used within an AdminRolePermissionProvider');
  }
  return context;
};

export const AdminRolePermissionProvider = ({ children }: { children: ReactNode }) => {
  const [allowedPageNames, setAllowedPageNames] = useState<Set<string>>(new Set());
  const [allowedActionsByPage, setAllowedActionsByPage] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);

  const reduxRoleId = useSelector((state: any) => state.auth.userInfo?.role_id);
  const roleId = reduxRoleId || localStorage.getItem('role_id');

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!roleId) {
        setLoading(false);
        return;
      }

      try {
        const response = await instance.get(`roles/getAdminRolePermissionByRoleId/${roleId}`);
        const data: PermissionData = response.data;

        const pages = new Set<string>();
        const actions = new Map<string, Set<string>>();

        if (data && data.data && data.data.pages) {
          data.data.pages.forEach((p) => {
            pages.add(p.page_name);
            const actionSet = new Set(p.actions.map(a => a.action.toLowerCase()));
            actions.set(p.page_name, actionSet);
          });
        }

        setAllowedPageNames(pages);
        setAllowedActionsByPage(actions);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [roleId]);

  const canViewPage = (pageName: string) => {
    return allowedPageNames.has(pageName);
  };

  const canPerformAction = (pageName: string, actionKey: string) => {
    const pageActions = allowedActionsByPage.get(pageName);
    return pageActions ? pageActions.has(actionKey.toLowerCase()) : false;
  };

  const filterSitemapRoutesByPermissions = (routes: RouteItems[]): RouteItems[] => {
    return routes
      .map((route) => {
        const filteredPages = route.pages.filter((page) => {
          const pageName = getPermissionPageNameForPathname(page.path || '');
          if (pageName) {
            return canViewPage(pageName);
          }
          return true; // If not mapped, keep it
        });

        if (filteredPages.length === 0) return null;

        return {
          ...route,
          pages: filteredPages,
        };
      })
      .filter((route): route is RouteItems => route !== null);
  };

  return (
    <AdminRolePermissionContext.Provider
      value={{
        allowedPageNames,
        allowedActionsByPage,
        loading,
        canViewPage,
        canPerformAction,
        filterSitemapRoutesByPermissions,
      }}
    >
      {children}
    </AdminRolePermissionContext.Provider>
  );
};

export const PermissionGate = ({ children }: { children: ReactNode }) => {
  const { canViewPage, canPerformAction, loading } = useAdminRolePermissions();
  const location = useLocation();

  if (loading) return null;

  const pageName = getPermissionPageNameForPathname(location.pathname);
  if (pageName && !canViewPage(pageName) && !isHomePath(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  const actionRequirement = getRequiredActionForPathname(location.pathname);
  if (actionRequirement && !canPerformAction(actionRequirement.pageName, actionRequirement.action)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export const Permission = ({
  pageName,
  actionKey,
  children,
}: {
  pageName: string;
  actionKey: string;
  children: ReactNode;
}) => {
  const { canPerformAction, loading } = useAdminRolePermissions();

  if (loading) {
    return null;
  }

  if (canPerformAction(pageName, actionKey)) {
    return <>{children}</>;
  }

  return null;
};
